/** lpMove.ts — LP 후보 → 조합 (2026-10-12 지도사 「LP 로서 필요 정보 받는 링크 · 미리 받아 놨다가 조합에 매칭되면 거기로 옮겨줘」)
 *  · lpCollect(lp)    : LP 후보 한 명의 카드(LP 본인 + 대표하는 회사) — 조합 자료수집 카드와 같은 항목
 *  · lpIntoProject(p, lp) : 조합에 넣은 새 프로젝트 — 등록정보 회사(소개 팀장님 포함) · 자료수집 LP 카드(미리 만든 포털 그대로) · 회사 카드 · 마스터 LP 줄
 *  순수 함수 — 저장은 부르는 쪽(Venture.tsx)
 */
import { newLP, coItems, rid, buildCollect, getCard, personalSection, itemFromPortal } from './collect';
import { blankCo } from './types';
import type { Project, Collect, Co, PersonCard } from './types';
import type { Lp, LpPortalItem } from './api';

const norm = (s: string | undefined) => (s || '').replace(/\s+/g, '').toLowerCase();
const lpName = (lp: Lp) => (lp.contact || '').replace(/\s*(대표|대표님|이사|부장|과장|팀장)$/, '').trim() || lp.company;

/** LP 후보의 자료수집 카드 묶음 — 포털 항목은 portalItems(c, 'lp:' + id) 로 */
export function lpCollect(lp: Lp): { c: Collect; ref: string } {
  const card = newLP(lpName(lp), lp.phone || '');
  const c: Collect = { gp: { name: 'GP', bizno: '', url: '', recips: [], items: [], sent: false, results: null }, lps: [card], cos: [] };
  if (lp.company && norm(lp.company) !== norm(card.name)) c.cos.push({ id: rid(), name: lp.company, bizno: '', rep: 'lp:' + card.id, items: coItems() });
  return { c, ref: 'lp:' + card.id };
}

/** 불러온 포털 항목(LP 후보 「항목 편집」 · 조합으로 옮길 때)을 LP 카드 · 회사 카드에 나눠 담는다(묶음 = section) — 없으면 기본 항목 (2026-11-01) */
export function collectFromPortal(lp: Lp, items: LpPortalItem[]): { c: Collect; ref: string } {
  const base = lpCollect(lp);
  if (!items.length) return base;
  const { c, ref } = base;
  const own = personalSection(ref);
  getCard(c, ref).items = items.filter((x) => !x.section || x.section === own).map(itemFromPortal);
  const groups = new Map<string, LpPortalItem[]>();
  items.filter((x) => x.section && x.section !== own).forEach((x) => { const k = String(x.section); groups.set(k, [...(groups.get(k) || []), x]); });
  c.cos = Array.from(groups.keys()).map((name) => {
    const list = groups.get(name) || [];
    const old = c.cos.find((co) => co.name === name);
    return { id: old ? old.id : rid(), name, bizno: old ? old.bizno : '', rep: ref, items: list.map(itemFromPortal) };
  });
  return { c, ref };
}

const emptyLpCard = (l: PersonCard) => !(l.name || '').trim() && !l.collectProjectId && !l.sent;

/** 이미 들어가 있으면(같은 포털 · 같은 이름) null */
/** pre — 그 LP 후보 포털의 지금 항목(「항목 편집」으로 고친 것 · Venture.tsx 가 불러와 넘김) · 없으면 기본 항목 */
export function lpIntoProject(p: Project, lp: Lp, pre?: LpPortalItem[]): Project | null {
  const np = JSON.parse(JSON.stringify(p)) as Project;
  const name = lpName(lp);
  // ① 자료수집 — 처음 열기 전이면 지금 등록정보로 먼저 만든다(새 회사는 아래에서 따로 넣어 두 번 생기지 않게)
  const c: Collect = np.collect ? np.collect : buildCollect(np);
  if (c.lps.some((l) => (lp.collect_pid && l.collectProjectId === lp.collect_pid) || (norm(l.name) === norm(name) && !!name))) return null;
  // 비어 있는 자리 LP(이름 · 포털 · 발송 없음)는 그 자리를 쓴다 — 마스터 줄도 같은 자리
  const slot = c.lps.findIndex(emptyLpCard);
  const card = newLP(name, lp.phone || '');
  const fromPortal = pre && pre.length ? collectFromPortal(lp, pre) : null;   // 고친 항목 그대로 옮긴다 — 기본 항목으로 덮어 포털이 되돌아가지 않게(2026-11-01)
  if (fromPortal) card.items = getCard(fromPortal.c, fromPortal.ref).items;
  if (lp.collect_pid) { card.collectProjectId = lp.collect_pid; card.url = lp.collect_pid; }
  if (slot >= 0) { card.id = c.lps[slot].id; c.lps[slot] = card; } else c.lps.push(card);
  const idx = slot >= 0 ? slot : c.lps.length - 1;
  if (fromPortal && fromPortal.c.cos.length) {
    fromPortal.c.cos.forEach((co) => { if (!c.cos.some((x) => norm(x.name) === norm(co.name))) c.cos.push({ ...co, id: rid(), rep: 'lp:' + card.id }); });
  } else if (lp.company && norm(lp.company) !== norm(name) && !c.cos.some((x) => norm(x.name) === norm(lp.company))) {
    c.cos.push({ id: rid(), name: lp.company, bizno: '', rep: 'lp:' + card.id, items: coItems() });
  }
  np.collect = c;
  // ② 등록정보 회사 — 소개 팀장님(FP) 정보까지
  const cos: Co[] = (np.cos || []).filter((x) => !!(x.name || x.bizno || x.ceo));
  if (lp.company && !cos.some((x) => norm(x.name) === norm(lp.company))) {
    cos.push({ ...blankCo(), name: lp.company, ceo: name, mgrTel: lp.phone || '', fpName: lp.referrer || '', fpTel: lp.referrer_phone || '', fpCo: lp.referrer_org || '', amt: lp.amount || '', ceoRole: 'lp' });   // LP 로 참여 — 업무관리 업무가 LP 순서(출자금 입금부터)로 (2026-10-18)
  }
  np.cos = cos;
  // ③ 마스터(조합원 명부) — 있으면 같은 자리에
  if (np.master && Array.isArray(np.master.lps)) {
    const row = { name, rrn: '', addr: '', amt: lp.amount || '', rel: '', tel: lp.phone || '', referrers: lp.referrer || '' };
    const cur = np.master.lps[idx];
    if (cur && !(cur.name || '').trim() && !(cur.rrn || '').trim()) np.master.lps[idx] = { ...cur, ...row };
    else if (np.master.lps.length === idx) np.master.lps.push(row);   // 자리가 어긋나 있으면 건드리지 않는다(마스터 화면에서 직접)
  }
  return np;
}
